/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useStorageState } from 'react-storage-hooks';
import { isEqual, noop, uniq } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import {
  PipelineTable,
  usePipelineReducer,
  useNoDataComponentType,
  useColumns,
  getUnderlyingSearchFields,
  defaultPageSize,
  Dropdown,
  BrandColors,
  PipelineTopic,
  BadgeValue,
  FiltersMenu,
  ActionButton,
} from '@blend/enterprise-components';
import { Pipeline } from '@blend/search-client';
import * as Sentry from '@sentry/browser';
import { useInterval } from 'blb-hooks/useInterval';
import { getPersona, isPipelinePersona } from 'blb-persona/index';
import {
  getLoansElasticSearch,
  putPipelineSaveEndpoint,
  getAvailableAssignees,
  patchPipelineSaveEndpoint,
} from 'blb-api/loans';
import { getConfig } from 'blb-utils/config';
import { MeContext } from 'blb-contexts/Me';
import { CenteredMessageOverlay } from 'blb-components/CenteredMessageOverlay';

import { PipelineColumnHeader } from './PipelineColumnHeader';
import { PipelineStateContext } from './PipelineStateContext';
import { getColumnConfigurations, PipelinePrincipalType } from './configs';
import { PipelinePageHeader } from './PipelinePageHeader';
import {
  DEFAULT_PIPELINE_STATE,
  DEFAULT_SORT_FIELD,
  getDefaultPipelineFiltersWithLoggedInUser,
  getFilters,
  getOriginalColNameFromSchema,
  getSortKey,
  PIPELINE_FILTERS_KEY,
  PIPELINE_STATE_KEY,
  PipelineState,
  sanitizePipelineState,
} from 'blb-utils/pipeline';
import { getOutstandingInvitations, getUserFeatures } from 'blb-api/users';
import { BlendBridge } from 'blb-types/blendBridge';
import { PIPELINE_ACTION_TIMEOUT, PIPELINE_INPUT_TIMEOUT } from './constants';
import TeamsInvitationModal from '../../TeamsPage/TeamsInvitationModal';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { FilterParams } from 'blb-constants/pipelineFilter';

export const styles = StyleSheet.create({
  centeredContainer: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
  },
  errorContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    top: '15%',
    width: '100%',
  },
  filtersMenuWrapper: {
    borderBottom: `1px solid ${BrandColors.CLOUD_50}`,
    paddingLeft: 40,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: `42px 40px`,
  },
  pipelineContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: `100%`,
  },
  pipelineTableWrapper: {
    flexGrow: 1,
    minHeight: 0,
  },
  rightContainer: {
    alignSelf: 'flex-end',
    display: 'flex',
    paddingRight: 40,
    gap: 16,
  },
  centerSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
  },
});

export namespace PipelinePage {
  export interface Props {}
}

interface PipelineConfiguration {
  canNavigateFromPipeline: boolean;
  columns: string[];
  filters: Pipeline.IFilter[];
}

export const PipelinePage: React.FC<PipelinePage.Props> = ({}) => {
  const me = React.useContext(MeContext)!;
  const [invitations, setInvitations] = useState<BlendBridge.API.GroupInvitationResponse[]>([]);
  const [isInvitationsModalOpen, setIsInvitationsModalOpen] = useState<boolean>(false);

  const handleInvitationsModalClose = () => {
    setIsInvitationsModalOpen(false);
  };

  const {
    pipelineSchema: { columnSchemas },
    enableAutoAssignment,
    isTeamsUIEnabled,
  } = getConfig();

  sanitizePipelineState();
  const [pipelineState, setPipelineState] = useStorageState<PipelineState>(
    sessionStorage,
    PIPELINE_STATE_KEY,
    DEFAULT_PIPELINE_STATE,
  );
  const [showOverlayMessage, setShowOverlayMessage] = useState<boolean>(false);
  const [filters, setFilters] = useStorageState<FilterParams>(
    sessionStorage,
    PIPELINE_FILTERS_KEY,
    getDefaultPipelineFiltersWithLoggedInUser(me?.principalId),
  );

  const [pipelineTabsFetched, setPipelineTabsFetched] = useState<boolean>(false);
  const [pipelineTabs, setPipelineTabs] = useState<{
    activeTabId: string;
    subjectsNoun?: string;
    tabs: BlendBridge.PipelineTab[];
  } | null>(null);

  const fetchInvitations = async () => {
    try {
      const invitations = await getOutstandingInvitations(me.principalId);
      setInvitations(invitations);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    setIsInvitationsModalOpen(invitations.length > 0);
  }, [invitations]);

  useEffect(() => {
    if (isTeamsUIEnabled) {
      void fetchInvitations();
    }
  }, []);

  /**
   * Use a ref to determine if pipeline is ready to make requests.
   * Without this we end up making the same request multiple times because the
   * PipelineTable component calls fetchPipelineTopics from its header component
   * when the sorting is changed (which means it is triggered by our default
   * sort one extra time), as well as from the table component on first render
   * and when the offset is changed, so we use this ref to help dictate when
   * requests are allowable.
   */
  const isRequestLockedRef: React.MutableRefObject<boolean> = React.useRef<boolean>(false);

  const getPipelineConfiguration = (): PipelineConfiguration => {
    const columnsForPrincipal = getColumnConfigurations(me.principalType as PipelinePrincipalType);
    const iFilter = getFilters(filters, me);
    const tab = pipelineTabs?.tabs.find(tab => tab.id === pipelineTabs.activeTabId);
    return {
      canNavigateFromPipeline: tab?.canNavigateFromPipeline ?? true,
      columns: tab?.columnsOverride || columnsForPrincipal,
      filters: tab?.filters || iFilter,
    };
  };

  const getEmptyTypeMap = (subjectsNoun = 'closings') => {
    return {
      empty: {
        header: `No ${subjectsNoun} assigned at this time`,
      },
      filter: {
        header: 'Try different filters to expand your search',
        subtext: `We didn't find any ${subjectsNoun} that match those filters.`,
      },
      loading: {
        header: `Loading ${subjectsNoun}...`,
      },
    };
  };

  const pipelineConfiguration = getPipelineConfiguration();
  const columnsForPrincipal = pipelineConfiguration.columns;
  const isOpsDesk = me.principalType === 'ops_desk_agent';

  const pipelineReducer = usePipelineReducer(DEFAULT_SORT_FIELD, { columnSchema: columnSchemas });
  const {
    state,
    getState,
    updateOffset,
    updateSortObj,
    fetchOnStart,
    fetchOnSuccess,
    fetchOnError,
    updateAlertMessage,
  } = pipelineReducer;

  useEffect(() => {
    // make the API call with filters + sorting + pagination
    // We're waiting on the response back from backend to determine
    // if we want to render tabs or not, and since fetching pipelineTopics
    // locks any subsequent request until the previous request is done
    // we want to make sure the initial fetch is correct
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    if (pipelineTabsFetched) {
      fetchPipelineTopics();
    }
  }, [filters, pipelineState, pipelineTabsFetched, pipelineTabs]);

  useEffect(() => {
    // totalCount gets updated twice on tab click
    // 1. totalCount = 0 & isFetching = false (loading state)
    // 2. totalCount = fetched totalCount & isFetching = true (totalCount fetched)
    // We're looking to invoke fetchPipelineTabCounts in scenario 2.
    if (pipelineTabsFetched && state.isFetching) {
      fetchPipelineTabCounts();
    }
  }, [state.totalCount]);

  const fetchPipelineTabCounts = async () => {
    // Fetch in scenarios where multiple tabs exist in Pipeline
    if (pipelineTabs?.tabs) {
      const { tabs } = pipelineTabs;
      await Promise.all(
        tabs.map(async (tab, index) => {
          // Stop from fetching count for current active tab
          // as we have it stored already
          if (pipelineTabs.activeTabId === tab.id) {
            tabs[index].subjectCount = state.totalCount;
          } else {
            const body: Pipeline.IQuery = {
              ...pipelineState,
              fields: [],
              filters: tab.filters,
              limit: 0,
            };

            const {
              data: { totalCount },
            } = await getLoansElasticSearch(body);
            tabs[index].subjectCount = totalCount;
          }
        }),
      );

      // Passing a copy causes it to consistently re-render
      // and update the count within the UI
      setPipelineTabs({ ...pipelineTabs });
    }
  };

  // Always update both offset AND sorting so that one update does not overwrite
  // the other.
  const getUpdatedSortAndOffset = React.useCallback(() => {
    const { sortObj, offset } = getState();
    const sortKey = getSortKey(columnSchemas, sortObj.sortField);
    setPipelineState({
      ...pipelineState,
      offset,
      sortDir: sortObj.sortDir,
      sortField: sortKey,
    });
  }, [getState]);

  // only responsible for making search request based on current "state"
  const fetchPipelineTopics = React.useCallback(async () => {
    if (!isRequestLockedRef.current) {
      isRequestLockedRef.current = true;
      fetchOnStart();
      const { columnSchema } = getState();

      const originalSortField = getOriginalColNameFromSchema(columnSchemas, pipelineState.sortField);
      const iFilter = pipelineConfiguration.filters;

      if (originalSortField) {
        const newSortObj: Pick<PipelineState, 'sortDir' | 'sortField'> = {
          sortDir: pipelineState.sortDir,
          sortField: originalSortField,
        };
        updateSortObj(newSortObj);
      }

      updateOffset(pipelineState.offset);

      const postBody: Pipeline.IQuery = {
        ...pipelineState,
        fields: uniq([...columnsForPrincipal, ...getUnderlyingSearchFields(columnSchema, columnsForPrincipal)]),
        filters: iFilter,
        limit: defaultPageSize,
      };
      let totalTopicsCount: undefined | number;
      try {
        const {
          data: { topics, totalCount },
        } = await getLoansElasticSearch(postBody);
        totalTopicsCount = totalCount;
        fetchOnSuccess({ topics, totalCount });
      } catch (error) {
        Sentry.captureException(error);
        fetchOnError();
      } finally {
        isRequestLockedRef.current = false;
      }
      return totalTopicsCount;
    }
  }, [
    fetchOnStart,
    fetchOnSuccess,
    fetchOnError,
    updateSortObj,
    updateOffset,
    getState,
    pipelineState,
    filters,
    pipelineTabs,
  ]);

  const isMountedRef = React.useRef(true);
  useEffect(() => {
    // Fetch pipelineTabs which in return renders <FiltersMenu /> if tabs exist.
    // Otherwise default to original pipeline view
    const fetchPipelineTabs = async () => {
      try {
        const {
          data: { pipelineRowAction },
        } = await getUserFeatures();
        if (pipelineRowAction.pipelineTabs) {
          const { pipelineTabs: tabs, subjectsNoun } = pipelineRowAction;
          setPipelineTabs({
            activeTabId: tabs[0].id,
            subjectsNoun,
            tabs,
          });
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setPipelineTabsFetched(true);
      }
    };

    fetchPipelineTabs();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const savePipelineInputData = async (
    event: React.KeyboardEvent | React.ChangeEvent,
    endpoint: string,
  ): Promise<void> => {
    const target = event.target as HTMLInputElement;
    try {
      const res = await putPipelineSaveEndpoint(endpoint, target.value);
      updateAlertMessage({ message: res.data.message, theme: 'info' });

      // Clear the message after a timeout IF the component is still mounted
      setTimeout(() => isMountedRef.current && updateAlertMessage({ message: '' }), PIPELINE_INPUT_TIMEOUT);
    } catch (error) {
      updateAlertMessage({ message: 'Failed to update', theme: 'error' });
      setTimeout(() => isMountedRef.current && updateAlertMessage({ message: '' }), PIPELINE_INPUT_TIMEOUT);

      if (enableAutoAssignment && target.type === 'select-one') {
        // Error state is only shown when focus is moved from target
        target.blur();
        throw new Error('Failed to update');
      }
    }
  };

  const onPipelineActionClick = async (endpoint: string, body: Record<string, any>): Promise<void> => {
    try {
      await patchPipelineSaveEndpoint(endpoint, body);
      updateAlertMessage({ message: 'Action successful', theme: 'info' });
    } catch (error) {
      updateAlertMessage({ message: 'Failed to update', theme: 'error' });
    } finally {
      // Clear the message after a timeout IF the component is still mounted
      setTimeout(() => isMountedRef.current && updateAlertMessage({ message: '' }), PIPELINE_ACTION_TIMEOUT);
    }
  };

  const isCellBold = (loan: PipelineTopic) => {
    return isOpsDesk && isRush(loan);
  };

  const isRush = (loan: PipelineTopic) => {
    if (loan.properties['Eclose.openTasksForOpsDesk']) {
      const tasks = (loan.properties['Eclose.openTasksForOpsDesk'] as BadgeValue).message;
      return tasks === 'RUSH';
    }

    return false;
  };

  const inputAction = React.useCallback(savePipelineInputData, [updateAlertMessage]);
  const linkAction = React.useCallback(onPipelineActionClick, [updateAlertMessage]);

  const fetchOptions = async (endpoint: string, currentValue?: string | number): Promise<Dropdown.Option[]> => {
    const res = await getAvailableAssignees(endpoint, (currentValue as string) || '');
    return res.data;
  };

  // poll for pipeline updates when the pipeline is empty
  useInterval(async () => {
    if (state.rows.length === 0) {
      await fetchPipelineTopics();
    }
  }, getPersona(me.principalType).getPipelinePollingInterval());

  const noDataType = useNoDataComponentType(state.isFetching);
  const columns = useColumns(
    columnsForPrincipal,
    state.columnSchema,
    pipelineConfiguration.canNavigateFromPipeline,
    getUpdatedSortAndOffset,
    noop,
    PipelineColumnHeader,
    inputAction,
    linkAction,
    fetchOptions,
    isCellBold,
  );

  // required to ensure rows get sorted properly
  const getRowKey = (_state: void, rowData: { original: { _id: string } }) => ({ key: rowData.original._id });

  const getRowStyles = (rowData: { original: PipelineTopic }) => {
    if (isCellBold(rowData.original)) {
      return {
        backgroundColor: BrandColors.ACCENT_YELLOW_07,
      };
    }

    return {};
  };

  const contextValue = React.useMemo(() => pipelineReducer, [pipelineReducer]);

  const resetPagination = () => setPipelineState({ ...pipelineState, offset: 0 });
  // By setting the topics/rows to empty array the PipelineTable will display
  // a loading state until we get a new response from fetchPipelineTopics
  const setToLoadingState = () => fetchOnSuccess({ topics: [], totalCount: 0 });

  const maybeSetFiltersAndResetPagination = (newFilters: FilterParams) => {
    if (!isEqual(newFilters, filters)) {
      setFilters(newFilters);
      resetPagination();
    }
  };

  const getFiltersMenuComponent = () =>
    pipelineTabs && (
      <div className={css(styles.filtersMenuWrapper)}>
        <FiltersMenu
          filterList={pipelineTabs.tabs.map(tab => {
            return {
              detail: tab.subjectCount?.toString() || '',
              id: tab.id,
              label: tab.name,
            };
          })}
          onSelect={tab => {
            setPipelineTabs({
              ...pipelineTabs,
              activeTabId: tab.id,
            });
            setToLoadingState();
          }}
          selectedFilterId={pipelineTabs.activeTabId}
        />
      </div>
    );

  const getSearchAndFilterComponent = () => {
    const persona = getPersona(me.principalType);
    return (
      <div className={css(styles.rightContainer)}>
        {isPipelinePersona(persona) && persona.renderPipelineFilter(filters, maybeSetFiltersAndResetPagination)}
        <ActionButton
          disabled={state.isFetching}
          icon="refresh"
          label="Refresh"
          onClick={() => fetchPipelineTopics()}
          theme="secondary"
        />
      </div>
    );
  };
  return (
    <PipelineStateContext.Provider value={contextValue}>
      {isTeamsUIEnabled && isInvitationsModalOpen && (
        <TeamsInvitationModal
          handleClose={handleInvitationsModalClose}
          invitations={invitations}
          isOpen={isInvitationsModalOpen}
        />
      )}
      <div className={css(styles.pipelineContainer)} data-it="pipeline">
        <PipelinePageHeader
          fetchPipelineTopics={fetchPipelineTopics}
          setShowAssignmentOverlay={setShowOverlayMessage}
        />
        {getSearchAndFilterComponent()}
        {getFiltersMenuComponent()}
        <div className={css(styles.pipelineTableWrapper)}>
          <PipelineTable
            canSelectRows={undefined}
            columns={columns}
            emptyType={noDataType}
            emptyTypeMap={getEmptyTypeMap(pipelineTabs?.subjectsNoun)}
            errorFetchingData={state.errorLoadingLoans}
            fetchPipelineTopics={getUpdatedSortAndOffset}
            getRowStyles={getRowStyles}
            getTrProps={getRowKey}
            hideTotalCount={!!pipelineTabs?.activeTabId}
            inputMessage={state.inputMessage}
            inputMessageTheme={state.inputMessageTheme}
            numberOfColChangeOverTime
            offset={state.offset}
            selectionsMap={undefined}
            setSelectionState={undefined}
            tableData={state.rows}
            tableDataNoun="loan"
            tableName="PipelineTable"
            totalDataCount={state.totalCount}
            updatePipelineOffset={updateOffset}
          />
          {showOverlayMessage && (
            <CenteredMessageOverlay
              boundingAreaCssSelector=".ReactTable"
              message="Updating assignments"
              showLoadingSpinner={true}
            />
          )}
        </div>
        {state.rows?.length > 0 && state.isFetching && (
          <div className={css(styles.centerSpinner)}>
            <CenteredLoadingSpinner />
          </div>
        )}
      </div>
    </PipelineStateContext.Provider>
  );
};
